import {
  Divider,
  EmptyState,
  FlexBox,
  IconButton,
  InputText,
  SelectionList,
  SelectionListChangeEvent,
  Skeleton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { colors, sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import Fuse from 'fuse.js';
import { DashboardFavorite } from 'lib-dashboard-types';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useCurrentUser } from 'utils/user';

import { useQueueDashboardQueueContext } from '../../queueDashboardQueue.context';

import styles from './queueDashboardQueueFavoriteToggle.module.scss';

export const QueueDashboardQueueFavoriteToggle = memo(
  function QueueDashboardQueueFavoriteToggle(): JSX.Element {
    const dispatch = useAppDispatch();
    const notify = useNotifications();
    const { user } = useCurrentUser();

    const { isSkeleton, ...context } = useQueueDashboardQueueContext();
    const queue = context.queue!;

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const popoverContent = useRef<HTMLDivElement>(null);

    useDebounce(() => setDebouncedSearchTerm(searchTerm), 300, [searchTerm]);

    const [updateFavorite, updateState] =
      dashboardApi.useUpdateDashboardFavoriteMutation();

    const { favorites, isLoading, isSuccess, isError, refetch } =
      dashboardApi.useReadDashboardFavoritesQuery(
        { dashboard: 'queue' },
        {
          selectFromResult: ({ data, ...rest }) => ({
            favorites:
              data?.data.favorites.filter(
                (favorite) => favorite.user.id === user.id,
              ) ?? [],
            ...rest,
          }),
        },
      );

    const filteredFavorites = useMemo((): readonly DashboardFavorite[] => {
      const minMatchCharLength = 2;

      if (debouncedSearchTerm.length < minMatchCharLength) {
        return favorites;
      }

      const fuse = new Fuse(favorites, {
        keys: ['name', 'description'],
        threshold: 0.2,
        minMatchCharLength,
        distance: 200,
      });

      return fuse.search(debouncedSearchTerm).map(({ item }) => item);
    }, [favorites, debouncedSearchTerm]);

    const handleClearSearch = () => {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    };

    const handleCreateFavorite = useCallback(async () => {
      await dispatch(
        appActions.showModal({
          modal: {
            type: 'dashboard:favorite',
            arg: {
              mode: 'create',
              params: {
                queue_ids: [queue.id],
              },
            },
          },
        }),
      );
    }, [dispatch, queue.id]);

    const handleChangeSingleFavorite = useCallback(
      async (favorite: DashboardFavorite) => {
        try {
          const queueIds = new Set(favorite.params.queue_ids ?? []);

          if (queueIds.has(queue.id)) {
            queueIds.delete(queue.id);
            ampli.dashboardQueueItemFavoriteToggled({ value: false });
          } else {
            queueIds.add(queue.id);
            ampli.dashboardQueueItemFavoriteToggled({ value: true });
          }

          await updateFavorite({
            id: favorite.id,
            favorite: {
              params: {
                ...favorite.params,
                queue_ids: Array.from(queueIds),
              },
            },
          }).unwrap();
        } catch {
          notify(
            {
              title: 'Error while updating the favorite.',
            },
            NotificationType.Error,
          );
        }
      },
      [notify, queue.id, updateFavorite],
    );

    const handleChangeMultipleFavorites = useCallback(
      async ({ selectedItem }: SelectionListChangeEvent<string>) => {
        const favorite = favorites.find(({ id }) => id === selectedItem.value);

        if (favorite) {
          await handleChangeSingleFavorite(favorite);
        }
      },
      [favorites, handleChangeSingleFavorite],
    );

    /**
     * Handles closing the popover and resetting state when it closes. It
     * explicitly ignores open events as opening the popover is determined by
     * the logic after fetching favorites.
     */

    /**
     * Handles opening or closing the popover.
     *
     * When opening, if there's only one favorite, we immediately add/remove the
     * queue from that favorite without actually opening the popover. If there
     * are multiple favorites, we open the popover to allow the user to select
     * which favorites to add/remove the queue from.
     *
     * When closing, we reset the search term and scroll position.
     */
    const handleOpenChange = useCallback(
      async (isOpening: boolean) => {
        try {
          if (isOpening) {
            if (favorites.length === 1) {
              handleChangeSingleFavorite(favorites[0]);
            } else {
              setIsOpen(true);
            }
          }

          if (!isOpening) {
            setIsOpen(false);
            setSearchTerm('');

            if (popoverContent.current) {
              popoverContent.current.scrollTop = 0;
            }
          }
        } catch (error) {
          notify(
            {
              title: 'Error while updating the favorite.',
            },
            NotificationType.Error,
          );
        }
      },
      [favorites, handleChangeSingleFavorite, notify],
    );

    const selectedFavoriteIds = useMemo(
      (): string[] =>
        favorites
          .filter((favorite) =>
            favorite.params.queue_ids?.some((id) => id === queue.id),
          )
          .map((favorite) => favorite.id),
      [favorites, queue.id],
    );

    const isAnyFavoriteSelected = selectedFavoriteIds.length > 0;
    const noFavoritesMatch = debouncedSearchTerm && !filteredFavorites.length;
    const noFavoritesExist = !debouncedSearchTerm && !favorites.length;

    if (isSkeleton) {
      return (
        <FlexBox
          justifyContent="center"
          alignItems="center"
          width="32px"
          height="32px"
        >
          <Skeleton height="18px" width="18px" />
        </FlexBox>
      );
    }

    return (
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger asChild>
          <IconButton
            variant="tertiary"
            icon={isAnyFavoriteSelected ? 'star-full' : 'star'}
            iconProps={{
              color: isAnyFavoriteSelected
                ? colors.orange[300]
                : colors.neutral[400],
            }}
            isLoading={!isOpen && (isLoading || updateState.isLoading)}
          />
        </Popover.Trigger>

        <Popover.Content
          className={styles.popover}
          side="bottom"
          align="start"
          sideOffset={parseInt(sizes.spacer.values[200])}
          ref={popoverContent}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Divider padding="300 300 0">
            <Text.Paragraph variant="2" color="tertiary">
              Add queue to favorites
            </Text.Paragraph>
          </Divider>

          <FlexBox padding="300" gap="200">
            <InputText
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search favorites..."
              suffix={{
                type: 'custom',
                value: searchTerm ? (
                  <FlexBox alignItems="center" padding="0 100">
                    <IconButton
                      variant="tertiary"
                      icon="close"
                      onClick={handleClearSearch}
                    />
                  </FlexBox>
                ) : null,
                noWrap: true,
              }}
              autoFocus
            />

            <Tooltip content="New favorite list">
              <IconButton
                icon="plus"
                variant="secondary"
                size="large"
                onClick={handleCreateFavorite}
              />
            </Tooltip>
          </FlexBox>

          <SelectionList
            className={styles.list}
            selectedValues={selectedFavoriteIds}
            onChange={handleChangeMultipleFavorites}
            defaultActiveItemIndex={0}
            isMultiple
          >
            {filteredFavorites.map((favorite) => (
              <SelectionList.Item
                key={favorite.id}
                value={favorite.id}
                className={styles.item}
              >
                {favorite.name}
              </SelectionList.Item>
            ))}

            {isLoading &&
              Array.from({ length: 7 }).map((_, i) => (
                <SelectionList.Item
                  key={i}
                  value={i}
                  className={styles.item}
                  noFixedSelectionStateSpace
                  isDisabled
                >
                  <Skeleton
                    height="16px"
                    style={{
                      width: `${50 + ((i * 37) % 50)}%`, // Randomizes skeleton widths
                    }}
                  />
                </SelectionList.Item>
              ))}

            {isSuccess && noFavoritesMatch && (
              <FlexBox as="li" padding="300" justifyContent="center">
                <EmptyState
                  icon="search"
                  title="No favorites found"
                  description="Try changing your search criteria."
                  buttonProps={{
                    variant: 'secondary',
                    children: 'Clear search',
                    onClick: handleClearSearch,
                  }}
                />
              </FlexBox>
            )}

            {isSuccess && noFavoritesExist && (
              <FlexBox as="li" padding="300" justifyContent="center">
                <FlexBox flexDirection="column" alignItems="center" gap="200">
                  <EmptyState
                    icon="star-full"
                    title="No favorites created yet"
                    description="Create favorite lists to quickly access your most important queues."
                    buttonProps={{
                      variant: 'primary',
                      children: 'New favorite list',
                      onClick: handleCreateFavorite,
                    }}
                  />
                </FlexBox>
              </FlexBox>
            )}

            {isError && noFavoritesExist && (
              <FlexBox as="li" padding="300" justifyContent="center">
                <EmptyState
                  icon="exclamation-circle-filled"
                  title="Error loading favorites"
                  description="There was an unexpected error while loading your favorites."
                  buttonProps={{
                    variant: 'secondary',
                    children: 'Try again',
                    onClick: refetch,
                  }}
                />
              </FlexBox>
            )}
          </SelectionList>
        </Popover.Content>
      </Popover.Root>
    );
  },
);
